import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLabOrder } from '../labs-order.page';
import { usePatchLabOrderV1LabsOrdersOrderIdPatch } from '../../../../api/generated-catch-backend/laboratory-orders/laboratory-orders';
import { ROUTE_LABS_ORDER_STEP_3, ROUTE_LABS_ORDER_STEP_5, ROUTE_CONSENT } from '../../../../constants/routes';
import { LabsOrderStepLayout } from '../components';
import type { PatientServiceCenter } from '../../../../api/generated-catch-backend/model';

export function Step4Consent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeOrder, isLoadingOrder, refetchOrder } = useLabOrder();
  const [hipaaAccepted, setHipaaAccepted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Get selected lab from navigation state (passed from step 3)
  const selectedLabFromState = (location.state as { selectedLab?: PatientServiceCenter })?.selectedLab;

  const patchOrderMutation = usePatchLabOrderV1LabsOrdersOrderIdPatch();

  // Redirect back if no active order or no selected PSC (from either backend or navigation state)
  useEffect(() => {
    if (!isLoadingOrder && (!activeOrder || (!activeOrder.patientServiceCenter && !selectedLabFromState))) {
      navigate(ROUTE_LABS_ORDER_STEP_3);
    }
  }, [isLoadingOrder, activeOrder, selectedLabFromState, navigate]);

  const handleBack = () => {
    navigate(ROUTE_LABS_ORDER_STEP_3);
  };

  const handleSubmit = async () => {
    if (!hipaaAccepted) {
      setErrorMessage('Please accept the HIPAA Authorization to continue');
      return;
    }

    if (!activeOrder || !activeOrder.orderId) {
      setErrorMessage('No active order found');
      return;
    }

    setErrorMessage(null);

    try {
      await patchOrderMutation.mutateAsync({
        orderId: activeOrder.orderId,
        data: {
          operation: 'confirm_order',
          data: {
            consentStatus: 'accepted',
          },
        },
      });

      // Refetch order to get updated state
      await refetchOrder();

      // Navigate to success page
      navigate(ROUTE_LABS_ORDER_STEP_5);
    } catch (error) {
      console.error('Error confirming order:', error);

      // Check if this is a fatal/server error (500+)
      const status = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined;

      const isFatalError = status && status >= 500;

      if (isFatalError) {
        // For fatal errors, navigate to step 5 with error flag
        navigate(`${ROUTE_LABS_ORDER_STEP_5}?error=true`);
        return;
      }

      // For retryable errors, extract error message and show inline
      let errorMsg = 'Failed to confirm order. Please try again.';
      if (error && typeof error === 'object' && 'response' in error) {
        const response = (error as { response?: { data?: { detail?: string } } }).response;
        if (response?.data?.detail) {
          errorMsg = response.data.detail;
        }
      }

      setErrorMessage(errorMsg);
    }
  };

  const openHipaaDoc = () => {
    window.open(ROUTE_CONSENT.replace(':slug', 'junction-hipaa-authorization'), '_blank');
  };

  // Show loading state
  if (isLoadingOrder || !activeOrder || (!activeOrder.patientServiceCenter && !selectedLabFromState)) {
    return (
      <LabsOrderStepLayout currentStep={4} onBack={handleBack}>
        <div className="text-center py-8">
          <p className="text-[#9E9E9E]">Loading...</p>
        </div>
      </LabsOrderStepLayout>
    );
  }

  // Use lab data from backend if available, otherwise use data from navigation state
  const lab = activeOrder.patientServiceCenter || selectedLabFromState!;

  return (
    <LabsOrderStepLayout currentStep={4} onBack={handleBack}>
      {/* Intro Text */}
      <p className="mb-6 text-[14px] leading-[170%] text-[#3B3B3B] opacity-80">
        Please confirm your lab visit.
      </p>

      {/* Selected Lab Card */}
      <div
        className="mb-6 rounded-lg p-6"
        style={{
          borderWidth: '2px',
          borderStyle: 'solid',
          borderColor: '#0C6A58',
        }}
      >
        <h3 className="text-[18px] font-bold leading-[24px] text-[#183344] mb-2">
          Please confirm your lab visit.
        </h3>

        <div className="mb-4">
          <h4 className="text-[16px] font-bold leading-[24px] text-[#183344] mb-1">
            {lab.metadata.labName || `Lab ${lab.siteCode}`}
          </h4>
          <p className="text-[14px] font-normal leading-[140%] text-[#63605D]">
            {lab.metadata.address}
            <br />
            {lab.metadata.city}, {lab.metadata.state} {lab.metadata.zip}
          </p>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-[14px] font-normal leading-[140%] text-[#63605D]">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="inline-block mr-1"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 0C5.24 0 3 2.24 3 5c0 3.75 5 11 5 11s5-7.25 5-11c0-2.76-2.24-5-5-5zm0 7c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"
                fill="#63605D"
              />
            </svg>
            {lab.distance.toFixed(1)} miles away
          </p>

          <button
            type="button"
            onClick={() => {
              const address = encodeURIComponent(
                `${lab.metadata.address}, ${lab.metadata.city}, ${lab.metadata.state} ${lab.metadata.zip}`
              );
              window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
            }}
            className="inline-flex items-center gap-1 text-[14px] font-bold leading-[140%] text-[#183344] hover:text-[#0D1F2D] transition-colors underline"
          >
            See Location
            <svg
              width="13"
              height="12"
              viewBox="0 0 13 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="inline-block"
            >
              <path
                d="M11.1668 1.33301L2.16683 10.333M11.1668 1.33301H3.66683M11.1668 1.33301V8.83301"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* HIPAA Checkbox */}
      <div className="mb-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={hipaaAccepted}
            onChange={(e) => setHipaaAccepted(e.target.checked)}
            className="mt-1 h-5 w-5 rounded border-2 border-[#BDBDBD] text-[#0C6A58] focus:ring-[#0C6A58] focus:ring-offset-0"
          />
          <span className="text-[14px] leading-[170%] text-[#3B3B3B]">
            By checking this box, I declare I have read and agree to terms of the{' '}
            <button
              type="button"
              onClick={openHipaaDoc}
              className="underline font-medium hover:text-[#0C6A58] transition-colors"
            >
              HIPAA Authorization
            </button>
            .
          </span>
        </label>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-[14px] text-red-800">
          {errorMessage}
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!hipaaAccepted || patchOrderMutation.isPending}
          className="w-full rounded-full bg-[#7CF1C7] px-8 py-3.5 text-center text-[17px] font-bold leading-[26px] text-[#183344] transition-all hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {patchOrderMutation.isPending ? 'Processing...' : 'Order Your Lab Visit'}
        </button>
      </div>
    </LabsOrderStepLayout>
  );
}
